/* =============================================================
   9 STRAP — Gallery color grouping controller
   Built 2026-09-09

   Problem this solves:
     - Products are Color x Size. Each color needs its own set of ~5 images.
     - Changing Color must swap the whole gallery set.
     - Changing Size must do NOTHING — no reset to slide 1, no reload.

   Approach:
     Shopify's variant->image link is 1:1 and cannot express "these five
     belong to Red", and the theme's hide_variants path is gutted by
       theme.css: .product__media--variant:not(:first-child){display:none}
     which allows only one variant slide to lay out at all.

     So: hide_variants is OFF, every media renders, and this controller owns
     visibility. Media are grouped by an alt-text tag ("[Red] ...") emitted
     as JSON by snippets/hst-gallery-color-groups.liquid.

     Nothing here touches the theme's variant lifecycle: no variant:change
     event, no Section Rendering API, no URL variant param. That is precisely
     why a Size change is inert — the pack picker simply doesn't call us.

   FIX 2026-09-09b:
     - Untagged media used to be universal, which left every unclaimed photo
       trailing the selected color. Inverted: untagged is now HIDDEN and
       universality is an explicit opt-in ([All]/[Any]/[Shared]/[Universal]),
       so only the size chart follows the color group.

   FIX 2026-09-09c — ROOT CAUSE of "first image shows twice on every color
   except Black":
     In gallery_layout 'scroll' the theme emits featured_media into BOTH
     sticky-element.product__preview AND the first slot of .product__media-list,
     then repaintPreview() writes the selected color's first image into the
     preview — so the preview and one list slide show the same photo.

     The previous dedup hid "the first non-hidden slide", i.e. it deduped by
     POSITION. That is only reliably the slide the preview is showing when the
     color is the Liquid-rendered featured media — Black. For every other color
     the duplicate sits mid-list, and a positional class on a mid-list element
     is also the kind of thing the theme's re-measure pass (which our own
     resize dispatch provokes) can clobber.

     Now deduped by IMAGE IDENTITY: read what the preview is actually
     displaying and hide whichever visible slide carries the same file. Robust
     to the preview showing something we didn't predict, and self-correcting if
     repaintPreview ever no-ops. Comparison goes through fileKey() because the
     same Shopify asset appears under different ?width= and _600x suffixes in
     the preview vs the slides — raw string equality never matches.

     Dedup also now runs AFTER the reflow settles, so the theme re-measures
     first and our class lands last.

   Two traps, both live-verified on this theme:
     * Slides inside display:none never fetch their images. is="lazy-image"
       keeps the real URL on data-src/data-srcset, so revealed slides get
       those promoted on first show.
     * <media-dots> children ARE the slider's index space (data-media-id +
       data-index, same order as slider.items). Hiding a slide without hiding
       its dot desynchronizes the two. The dedup pass is deliberately NOT
       applied to dots: the deduped slide's image is still on screen (in the
       preview), so its dot must remain.
   ============================================================= */

(function () {
  'use strict';

  const HIDDEN_CLASS = 'hst-media-hidden';
  const DUP_CLASS = 'hst-media-dup';
  const UNIVERSAL_TAGS = ['all', 'any', 'shared', 'universal'];

  const dataEl = document.getElementById('hst-gallery-color-data');
  if (!dataEl) return;

  let DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.warn('hst-gallery-color: bad JSON payload', e);
    return;
  }

  function norm(s) {
    return (s == null ? '' : String(s)).trim().toLowerCase();
  }

  // media id (string) -> tag ('' = untagged = hidden)
  const TAG_BY_ID = {};
  let hasAnyTag = false;
  for (const m of (DATA.media || [])) {
    const tag = norm(m.tag);
    TAG_BY_ID[String(m.id)] = tag;
    if (tag) hasAnyTag = true;
  }

  // Nothing tagged in the admin yet — stay completely out of the way rather
  // than hiding the entire gallery under the untagged=hidden rule.
  if (!hasAnyTag) return;

  function gallery() {
    return document.querySelector('media-gallery[id^="ProductGallery-"]');
  }

  /* -----------------------------------------------------------
     Base filename of a Shopify CDN URL, minus size suffix and query string.
     The preview and the slides request different render widths of the same
     asset, so this is the only comparison that identifies "same image".
     ----------------------------------------------------------- */
  function fileKey(url) {
    if (!url) return '';
    const path = String(url).split('?')[0];
    const name = path.substring(path.lastIndexOf('/') + 1);
    return name.replace(/_\d+x\d*(_crop_[a-z]+)?(?=\.[a-z]+$)/i, '');
  }

  // Whatever URL an <img> is actually going to render, lazy state included.
  function imgUrl(img) {
    if (!img) return '';
    return img.getAttribute('src') || img.getAttribute('data-src') || '';
  }

  /* -----------------------------------------------------------
     Index the two node sets the theme keeps in lockstep:
       slides -> .product__media-list children
       dots   -> <media-dots> children (product-thumbnail buttons)
     Both carry data-media-id, which is the only reliable join key
     (image ids from /products/<handle>.json are a different namespace).
     ----------------------------------------------------------- */
  function collectSlides(g) {
    const list = g.querySelector('.product__media-list');
    if (!list) return [];
    return Array.prototype.slice.call(list.children).map(child => {
      const holder = child.matches('[data-media-id]')
        ? child
        : child.querySelector('[data-media-id]');
      return {
        el: child,
        id: holder ? String(holder.getAttribute('data-media-id')) : null
      };
    }).filter(s => s.id);
  }

  function collectDots(g) {
    const dots = g.querySelector('media-dots');
    if (!dots) return [];
    return Array.prototype.slice.call(dots.children).map(child => {
      const holder = child.matches('[data-media-id]')
        ? child
        : child.querySelector('[data-media-id]');
      return {
        el: child,
        id: holder ? String(holder.getAttribute('data-media-id')) : null
      };
    }).filter(s => s.id);
  }

  /* -----------------------------------------------------------
     Visibility rule:
       unknown media (not in the map) -> shown, we don't manage it
       [All]/[Any]/[Shared]/[Universal] -> shown for every color
       untagged ('')                  -> HIDDEN
       tagged                         -> shown only on exact color match
     ----------------------------------------------------------- */
  function isVisibleForColor(mediaId, color) {
    const tag = TAG_BY_ID[mediaId];
    if (tag === undefined) return true;
    if (UNIVERSAL_TAGS.indexOf(tag) !== -1) return true;
    if (!color) return true;   // no color resolved yet — show everything
    if (!tag) return false;    // untagged
    return tag === norm(color);
  }

  /* -----------------------------------------------------------
     Lazy images inside display:none never load. is="lazy-image" parks the
     real URL on data-src / data-srcset until it decides to swap. When we
     reveal a slide we promote those immediately so there is no blank frame.
     ----------------------------------------------------------- */
  function wakeImages(scope) {
    const imgs = scope.querySelectorAll('img');
    for (const img of imgs) {
      const dSrcset = img.getAttribute('data-srcset');
      const dSrc = img.getAttribute('data-src');
      if (dSrcset && !img.getAttribute('srcset')) img.setAttribute('srcset', dSrcset);
      if (dSrc && !img.getAttribute('src')) img.setAttribute('src', dSrc);
      if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
    }
  }

  function previewImg(g) {
    return g.querySelector('.product__preview .product__media img');
  }

  // Is the sticky preview actually laid out right now? Computed style, because
  // the theme gates it with a Tailwind utility whose breakpoint we should not
  // be hardcoding here.
  function previewIsLive(g) {
    const p = g.querySelector('.product__preview');
    if (!p) return false;
    try {
      return window.getComputedStyle(p).display !== 'none';
    } catch (e) {
      return false;
    }
  }

  /* -----------------------------------------------------------
     Desktop sticky preview (gallery_layout: 'scroll') renders the featured
     media only and lives outside .product__media-list, so it needs the
     first visible image of the group copied into it.
     ----------------------------------------------------------- */
  function repaintPreview(g, firstVisibleSlideEl) {
    const preview = previewImg(g);
    if (!preview || !firstVisibleSlideEl) return;
    const source = firstVisibleSlideEl.querySelector('img');
    if (!source) return;

    const src = imgUrl(source);
    const srcset = source.getAttribute('srcset') || source.getAttribute('data-srcset');
    if (!src) return;
    if (fileKey(preview.getAttribute('src')) === fileKey(src)) return;

    preview.removeAttribute('data-srcset');
    if (srcset) {
      preview.setAttribute('srcset', srcset);
    } else {
      preview.removeAttribute('srcset');
    }
    preview.setAttribute('src', src);
  }

  /* -----------------------------------------------------------
     Hide the visible slide that duplicates whatever the preview is currently
     displaying — matched by filename, not by position. Only while the preview
     is on screen. Dots are untouched: the image is still visible in the
     preview, so its dot should stay and the slider's index space stays whole.
     ----------------------------------------------------------- */
  function applyPreviewDedup(g, slides) {
    slides = slides || collectSlides(g);

    if (!previewIsLive(g)) {
      for (const s of slides) s.el.classList.remove(DUP_CLASS);
      return;
    }

    const pKey = fileKey(imgUrl(previewImg(g)));
    let matched = false;

    for (const s of slides) {
      if (s.el.classList.contains(HIDDEN_CLASS)) {
        s.el.classList.remove(DUP_CLASS);
        continue;
      }
      const key = fileKey(imgUrl(s.el.querySelector('img')));
      const isDup = !matched && pKey !== '' && key === pKey;
      s.el.classList.toggle(DUP_CLASS, isDup);
      if (isDup) matched = true;
    }

    // Preview shows something no visible slide carries (repaint no-op'd, or an
    // unknown media). Fall back to positional dedup so the hero is never blank
    // and the grid never leads with a second copy.
    if (!matched && pKey === '') {
      let first = true;
      for (const s of slides) {
        if (s.el.classList.contains(HIDDEN_CLASS)) continue;
        s.el.classList.toggle(DUP_CLASS, first);
        first = false;
      }
    }
  }

  /* -----------------------------------------------------------
     Return the carousel to slide 1 of the NEW set and let slider-element
     recompute. Its itemsToShow / maxScroll are derived from laid-out boxes,
     so a resize dispatch after the class toggle is what rebuilds them.
     We never call select() or touch currentPage — that coordinate space is
     exactly what broke the original implementation.
     ----------------------------------------------------------- */
  function resetPosition(g) {
    const candidates = [
      g.querySelector('.product__media-list'),
      g.querySelector('slider-element'),
      g.querySelector('.product__thumbnails-list')
    ];
    for (const el of candidates) {
      if (!el) continue;
      try {
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        } else {
          el.scrollLeft = 0;
        }
      } catch (e) { /* non-scrolling container */ }
    }

    requestAnimationFrame(() => {
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    });
  }

  let appliedColor = null;

  function setColor(color) {
    try {
      const g = gallery();
      if (!g) return;

      const slides = collectSlides(g);
      if (!slides.length) return;
      const dots = collectDots(g);

      let firstVisible = null;
      let shownCount = 0;

      for (const s of slides) {
        const show = isVisibleForColor(s.id, color);
        if (show) {
          shownCount++;
          if (!firstVisible) firstVisible = s.el;
        }
        s.el.classList.toggle(HIDDEN_CLASS, !show);
      }

      // Safety net: a color with zero matching images would empty the gallery.
      // Show everything rather than render a blank box. Most likely cause is a
      // tag that doesn't match the Color option value.
      if (shownCount === 0) {
        for (const s of slides) {
          s.el.classList.remove(HIDDEN_CLASS);
          s.el.classList.remove(DUP_CLASS);
        }
        for (const d of dots) d.el.classList.remove(HIDDEN_CLASS);
        g.removeAttribute('data-hst-color-groups');
        appliedColor = color;
        return;
      }

      for (const d of dots) {
        d.el.classList.toggle(HIDDEN_CLASS, !isVisibleForColor(d.id, color));
      }

      // Wake images BEFORE the preview repaint — the source slide's <img>
      // needs a real URL on it for both the copy and the filename match.
      for (const s of slides) {
        if (!s.el.classList.contains(HIDDEN_CLASS)) wakeImages(s.el);
      }

      repaintPreview(g, firstVisible);
      applyPreviewDedup(g, slides);
      resetPosition(g);

      // Re-apply after the theme's own re-measure (provoked by resetPosition's
      // resize dispatch) has settled, so our class is the last word.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try { applyPreviewDedup(gallery()); } catch (e) {}
        });
      });

      g.removeAttribute('data-hst-color-groups');
      appliedColor = color;
    } catch (e) {
      // Never let a gallery markup change take down the buy box.
      console.warn('hst-gallery-color: setColor failed', e);
      const g = gallery();
      if (g) g.removeAttribute('data-hst-color-groups');
    }
  }

  function currentColor() {
    return appliedColor;
  }

  // Crossing the preview breakpoint flips whether a slide is a duplicate.
  let resizeTimer = null;
  function onResize() {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      try {
        const g = gallery();
        if (g) applyPreviewDedup(g);
      } catch (e) {
        console.warn('hst-gallery-color: resize dedup failed', e);
      }
    }, 150);
  }

  window.HSTGalleryColor = { setColor, currentColor };

  function init() {
    const g = gallery();
    if (!g) return;
    g.setAttribute('data-hst-color-groups', 'pending');
    setColor(DATA.defaultColor || null);
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();